import { SlidersHorizontal } from 'lucide-react';

import useBowlingCentersStore from '@/shared/store/bowlingCenters';
import useSettingsStore from '@/shared/store/settings';
import { cn, getPlayerId } from '@/shared/utils';

import FollowPlayers from '@/components/livescoring/Live/followPlayers';
import Button from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import {
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
} from '@/components/ui/toggle-group';

const SETTINGS = [
  {
    name: 'Hide inactive lanes',
    value: 'hideInactiveLanes',
  },
  {
    name: 'Include handicaps',
    value: 'includeHCP',
  },
  {
    name: 'Include current game in total',
    value: 'includeGameTotal',
  },
  {
    name: 'Show pin leaves',
    value: 'showPinLeaves',
  },
];

const Customize = ({ onHideInactiveLanes, onFilterLanes, players }) => {
  const {
    columns,
    maxColumns,
    lanes,
    followPlayers,
    onUpdateSettings,
    ...restStore
  } = useSettingsStore();
  const { current } = useBowlingCentersStore();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button className="p-4 text-white hover:text-white border-none md:py-3">
          <SlidersHorizontal className="size-4" />
          <span className="hidden md:block">Customize</span>
        </Button>
      </SheetTrigger>

      <SheetContent className="px-2 pt-4 border-none bg-[#15161F] sm:w-96">
        <div className="bg-[#15161F] pt-1 pb-4 px-4">
          <h2 className="text-2xl font-semibold">Customize</h2>
        </div>

        <ScrollArea className="h-full pb-[max(calc(env(safe-area-inset-bottom)+1rem),48px)]">
          <div className="w-full space-y-4 px-4">
            {/* COLUMNS */}
            <section className="space-y-2 hidden sm:block">
              <span>Columns</span>
              <ToggleGroup
                type="single"
                className="gap-0 overflow-hidden h-9 w-full"
                value={columns}
                onValueChange={(value) => {
                  if (value === columns || value === '') return;
                  onUpdateSettings('columns', value);
                }}
              >
                {Array.from({ length: maxColumns }, (_, index) => (
                  <ToggleGroupItem
                    value={index + 1}
                    key={index + 1}
                    asChild
                    className={cn(
                      'rounded-none border-t-2 border-b-2 w-full',
                      index === 0 && 'rounded-l-md border-l-2',
                      index === maxColumns - 1 && 'rounded-r-md border-r-2'
                    )}
                    variant="outline"
                    size="sm"
                  >
                    <Button variant="icon">{index + 1}</Button>
                  </ToggleGroupItem>
                ))}
              </ToggleGroup>
            </section>
            {/* SETTINGS */}
            <section className="border-t border-[#a1a1a166] pt-2">
              {SETTINGS.map((setting) => (
                <div
                  key={setting.value}
                  className={cn(
                    'flex items-center justify-between cursor-pointer',
                    'py-2 px-4 -mx-4 hover:bg-primary/10 hover:transition-colors',
                    setting.value === 'hideInactiveLanes' &&
                      players.length === 0 &&
                      'opacity-50 cursor-not-allowed pointer-events-none'
                  )}
                  onClick={() => {
                    if (setting.value === 'hideInactiveLanes')
                      return onHideInactiveLanes();

                    onUpdateSettings(setting.value, !restStore[setting.value]);
                  }}
                >
                  <span>{setting.name}</span>
                  <Switch checked={restStore[setting.value]} />
                </div>
              ))}
            </section>
            {/* FILTER LANES */}
            <section className="border-t border-[#a1a1a166] pt-2 space-y-2">
              <div className="flex items-center justify-between text-xl font-semibold text-secondary">
                Filter lanes
                <Button
                  variant="link"
                  disabled={lanes.length === 0}
                  onClick={() => {
                    onUpdateSettings('lanes', []);
                    onUpdateSettings('hideInactiveLanes', false);
                  }}
                >
                  Reset
                </Button>
              </div>
              <div className="flex gap-1 flex-wrap">
                {Array(current.lanes)
                  .fill(0)
                  .map((_, index) => (
                    <Toggle
                      key={index}
                      value={index}
                      asChild
                      className="data-[state=on]:bg-primary/30 data-[state=on]:text-white data-[state=on]:border-primary data-[state=on]:border-2"
                      pressed={lanes.includes(index + 1)}
                      onPressedChange={() => onFilterLanes(index + 1)}
                    >
                      <Button className="hover:text-black hover:bg-secondary border-secondary size-11 rounded-[10px] text-xl text-secondary">
                        {index + 1}
                      </Button>
                    </Toggle>
                  ))}
              </div>
            </section>
            {/* FOLLOW PLAYERS */}
            <section className="border-t border-[#a1a1a166] pt-2 space-y-2">
              <FollowPlayers
                players={players.map((player) => ({
                  name: player.playerName,
                  id: getPlayerId(player),
                  lane: player.lane,
                }))}
                selected={followPlayers}
                onChange={(playerId) => {
                  if (followPlayers.includes(playerId)) {
                    onUpdateSettings(
                      'followPlayers',
                      followPlayers.filter((id) => id !== playerId)
                    );
                  } else {
                    onUpdateSettings('followPlayers', [
                      ...followPlayers,
                      playerId,
                    ]);
                  }
                }}
                onReset={() => onUpdateSettings('followPlayers', [])}
              />
            </section>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default Customize;
